// Hover job card on the deal Overview (Oct 1, 2026).
// Shows where the Hover job is (waiting on photos → measuring → ready), the
// measurements once they're back, and the PDF. A job is created automatically
// when the claim is filed; "Create Hover job" covers deals filed before the
// integration existed (or a job that failed to create).

import { useCallback, useEffect, useState } from "react";
import { supabase } from "../../lib/supabase";
import { useRealtimeTable } from "../../lib/useRealtimeTable";
import { fetchHoverJobs, hoverSync, HOVER_STATE_META, type HoverJob } from "../../lib/hover";

function ago(iso: string | null) {
  if (!iso) return "";
  const m = Math.round((Date.now() - Date.parse(iso)) / 60000);
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 48) return `${h}h ago`;
  return `${Math.round(h / 24)}d ago`;
}

export default function HoverJobCard({ dealId, hoverLink }: { dealId: string; hoverLink?: string | null }) {
  const [jobs, setJobs] = useState<HoverJob[] | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [msg, setMsg] = useState<{ kind: "ok" | "err"; text: string } | null>(null);

  const load = useCallback(async () => {
    try { setJobs(await fetchHoverJobs(dealId)); } catch { setJobs([]); }
  }, [dealId]);
  useEffect(() => { void load(); }, [load]);
  useRealtimeTable("hover_jobs", load);

  const job = jobs?.find((j) => j.state !== "error") ?? jobs?.[0] ?? null;

  async function run(action: string) {
    setBusy(action); setMsg(null);
    try {
      const r = await hoverSync<any>({ action, deal_id: dealId });
      if (r?.skipped === "not_connected") setMsg({ kind: "err", text: "Hover isn't connected yet — an admin connects it once at Integrations → Hover." });
      else if (r?.skipped === "missing_address") setMsg({ kind: "err", text: "This deal needs a street + city before Hover can create a job." });
      else if (r?.ok === false) setMsg({ kind: "err", text: r.detail ?? r.error ?? "Hover request failed" });
      else if (action === "create_job") setMsg({ kind: "ok", text: `Hover job #${r.hover_job_id} created — it's in ${r.assigned_email}'s Hover app.${r.note ? " " + r.note : ""}` });
      else if (action === "refresh_job") setMsg({ kind: "ok", text: r.state === "complete" ? "Measurements pulled in." : `Still ${HOVER_STATE_META[r.state as keyof typeof HOVER_STATE_META]?.label.toLowerCase() ?? r.state}.` });
      await load();
    } catch (e) {
      setMsg({ kind: "err", text: (e as Error).message });
    } finally { setBusy(null); }
  }

  async function openPdf(path: string) {
    const w = window.open("about:blank", "_blank");
    const { data } = await supabase.storage.from("deal-documents").createSignedUrl(path, 3600);
    if (data?.signedUrl && w) w.location.href = data.signedUrl; else w?.close();
  }

  if (jobs === null) return null;
  const meta = job ? HOVER_STATE_META[job.state] : null;
  const m = job?.state === "complete" ? job.measurements : null;
  const link = job?.hover_job_id ? `https://hover.to/ui/#/project/${job.hover_job_id}` : hoverLink;

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          <span className="text-lg">🏠</span>
          <h3 className="font-semibold text-slate-800">Hover measurements</h3>
          {meta && <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${meta.cls}`}>{meta.label}</span>}
        </div>
        <div className="flex items-center gap-2">
          {link && <a href={link} target="_blank" rel="noreferrer" className="text-xs text-sky-700 underline">Open in Hover</a>}
          {job && job.hover_job_id && job.state !== "complete" && (
            <button onClick={() => run("refresh_job")} disabled={!!busy}
              className="text-xs px-2.5 py-1 rounded-lg border border-slate-300 hover:bg-slate-50 disabled:opacity-50">
              {busy === "refresh_job" ? "Checking…" : "Check now"}
            </button>
          )}
          {(!job || job.state === "error" || job.state === "failed") && (
            <button onClick={() => run("create_job")} disabled={!!busy}
              className="text-xs px-3 py-1 rounded-lg bg-slate-900 text-white font-semibold disabled:opacity-50">
              {busy === "create_job" ? "Creating…" : job ? "Try again" : "Create Hover job"}
            </button>
          )}
        </div>
      </div>

      {!job && (
        <p className="text-xs text-slate-500 mt-2">
          No Hover job yet. One is created automatically when the claim is filed and sent to the rep's Hover app.
        </p>
      )}

      {job && job.state !== "complete" && (
        <p className="text-xs text-slate-500 mt-2">
          {job.hover_job_id ? <>Job #{job.hover_job_id} · in <strong>{job.assigned_email}</strong>'s Hover app</> : "Creating the Hover job…"}
          {job.state === "capturing" && " — waiting for the rep to take the photos."}
          {job.state === "processing" && " — photos are in, Hover is building the model (usually a few hours)."}
          {job.last_checked_at && <span className="text-slate-400"> · checked {ago(job.last_checked_at)}</span>}
        </p>
      )}
      {job?.error && <p className="text-xs text-rose-700 mt-1">{job.error}</p>}

      {m && (
        <div className="mt-3">
          <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
            {[
              ["Sq w/ waste", m.sq_with_waste, ` (${m.waste_pct}%)`],
              ["Squares", m.squares, ""],
              ["Eaves", m.eaves, "′"],
              ["Rakes", m.rakes, "′"],
              [m.ridges_hips_combined ? "Ridges+hips" : "Ridges", m.ridges, "′"],
              ["Hips", m.ridges_hips_combined ? null : m.hips, "′"],
              ["Valleys", m.valleys, "′"],
              ["Flashing", m.flashing, "′"],
              ["Step flash", m.step_flashing, "′"],
              ["Pitch", m.predominant_pitch, ""],
            ].filter(([, v]) => v != null).map(([label, v, unit]) => (
              <div key={label as string} className="bg-slate-50 rounded-lg px-2 py-1.5">
                <div className="text-[10px] uppercase tracking-wide text-slate-500">{label}</div>
                <div className="text-sm font-semibold text-slate-800">{String(v)}{unit === "′" ? "′" : ""}{unit && unit !== "′" ? <span className="text-[10px] text-slate-400">{unit}</span> : null}</div>
              </div>
            ))}
          </div>
          <div className="flex items-center gap-3 mt-2 text-xs">
            {job?.pdf_storage_path && (
              <button onClick={() => openPdf(job.pdf_storage_path!)} className="text-sky-700 underline">📄 Hover report (PDF)</button>
            )}
            <span className="text-slate-500">
              {job?.measurements_applied_at ? "Filled into the PM Board (empty fields only)." : ""} Ready {ago(job?.completed_at ?? null)}
            </span>
            <button onClick={() => run("apply_measurements")} disabled={!!busy} className="ml-auto text-slate-600 underline disabled:opacity-50">
              Fill PM Board again
            </button>
          </div>
        </div>
      )}

      {msg && <p className={`text-xs mt-2 ${msg.kind === "ok" ? "text-emerald-700" : "text-rose-700"}`}>{msg.text}</p>}
    </div>
  );
}
